'use client';

import { IconButton } from '../../../components/icon-button';
import { SettingsRow } from '../SettingsRow';
import { toasterApi } from '../../toast/ToastProvider';
import { BodyDefault, MonoSmall } from '../../../components/typography';
import { Tooltip } from '../../../components/tooltip';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  DEFAULT_CVSS_ENVIRONMENTAL_REQUIREMENTS,
  environmentalRequirementsForVersion,
  formatEnvironmentalVector,
  type CvssEnvironmentalRequirements,
} from '@terra/shared-cvss';
import { Tick01Icon, Copy01Icon, Edit03Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { EditCvssScoreDialog } from './EditCvssScoreDialog';
import type { CvssVersionSetting } from './cvss-scoring-metrics';

export type CvssSettingsPatch = {
  cvssVersion?: CvssVersionSetting;
  cvssEnvironmentalRequirements?: CvssEnvironmentalRequirements | null;
};
export type CvssScoringSectionProps = {
  settings?: {
    cvssVersion?: 'V3_1' | 'V4_0' | null;
    cvssEnvironmentalRequirements?: Partial<CvssEnvironmentalRequirements> | null;
  } | null;
  isPending?: boolean;
  isReadOnly?: boolean;
  showRescoreWarning?: boolean;
  onSave: (patch: CvssSettingsPatch) => Promise<void>;
};
const CIA_KEYS = ['cr', 'ir', 'ar'] as const;
const REQUIREMENT_KEYS = Object.keys(
  DEFAULT_CVSS_ENVIRONMENTAL_REQUIREMENTS,
) as Array<keyof CvssEnvironmentalRequirements>;
const areRequirementsEqual = ({
  left,
  right,
}: {
  left: CvssEnvironmentalRequirements;
  right: CvssEnvironmentalRequirements;
}): boolean => REQUIREMENT_KEYS.every((key) => left[key] === right[key]);

export const CvssScoringSection = ({
  settings,
  isPending = false,
  isReadOnly,
  showRescoreWarning = true,
  onSave,
}: CvssScoringSectionProps) => {
  const t = useTranslations('designSystem.settings.cvssScoring');
  const tCommon = useTranslations('designSystem.common');
  const [isSaving, setIsSaving] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [draftVersion, setDraftVersion] = useState<CvssVersionSetting>('V4_0');
  const [draftRequirements, setDraftRequirements] =
    useState<CvssEnvironmentalRequirements>(
      DEFAULT_CVSS_ENVIRONMENTAL_REQUIREMENTS,
    );
  const [copiedPreview, setCopiedPreview] = useState(false);
  const cvssVersion: CvssVersionSetting =
    settings?.cvssVersion === 'V3_1' ? 'V3_1' : 'V4_0';
  const requirements = environmentalRequirementsForVersion({
    requirements: {
      ...DEFAULT_CVSS_ENVIRONMENTAL_REQUIREMENTS,
      ...settings?.cvssEnvironmentalRequirements,
    },
    version: cvssVersion,
  });
  const versionLabel =
    cvssVersion === 'V4_0' ? t('versions.v40') : t('versions.v31');
  const environmentalVector = formatEnvironmentalVector(
    requirements,
    cvssVersion,
  );
  const scorePreview = environmentalVector
    ? `CVSS:${versionLabel}/${environmentalVector}`
    : `CVSS:${versionLabel}`;
  const isDirty =
    draftVersion !== cvssVersion ||
    !areRequirementsEqual({
      left: draftRequirements,
      right: requirements,
    });
  const save = async (body: CvssSettingsPatch): Promise<boolean> => {
    setIsSaving(true);

    try {
      await onSave(body);
      toasterApi.success({ title: t('saveSuccess') });

      return true;
    } catch (error: unknown) {
      console.error('Failed to update CVSS scoring settings', error);
      toasterApi.error({ title: t('saveError') });

      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const openDialog = () => {
    setDraftVersion(cvssVersion);
    setDraftRequirements(requirements);
    setIsDialogOpen(true);
  };

  const handleVersionChange = (version: CvssVersionSetting) => {
    setDraftVersion(version);
    setDraftRequirements((current) =>
      environmentalRequirementsForVersion({
        requirements: current,
        version,
      }),
    );
  };

  const saveDialog = async () => {
    if (!isDirty) {
      setIsDialogOpen(false);

      return;
    }

    const succeeded = await save({
      cvssVersion: draftVersion,
      cvssEnvironmentalRequirements: environmentalRequirementsForVersion({
        requirements: draftRequirements,
        version: draftVersion,
      }),
    });

    if (succeeded) {
      setIsDialogOpen(false);
    }
  };

  const copyScorePreview = async () => {
    try {
      await navigator.clipboard.writeText(scorePreview);
      setCopiedPreview(true);
      window.setTimeout(() => setCopiedPreview(false), 2000);
    } catch (error: unknown) {
      console.error('Failed to copy CVSS score preview', error);
    }
  };

  return (
    <VStack alignItems="stretch" gap={4} w="full">
      <Box
        w="full"
        bg="bg.neutral.default"
        borderWidth="1px"
        borderColor="border.neutral.subtle"
        borderRadius="lg"
        p={4}
      >
        <VStack alignItems="stretch" gap={4} w="full">
          <HStack
            justifyContent="space-between"
            alignItems="flex-start"
            gap={4}
          >
            <VStack alignItems="flex-start" gap={1} flex={1} minW={0}>
              <BodyDefault fontWeight="medium" color="fg.neutral.default">
                {t('title')}
              </BodyDefault>
              <BodyDefault color="fg.neutral.muted">
                {t('description')}
              </BodyDefault>
            </VStack>
            {!isReadOnly ? (
              <Tooltip
                title={tCommon('edit')}
                positioning={{ strategy: 'fixed' }}
              >
                <IconButton
                  variant="ghost"
                  size="sm"
                  aria-label={tCommon('edit')}
                  onClick={openDialog}
                  disabled={isPending || isSaving}
                  h={8}
                  minW={8}
                  px={2}
                >
                  <Edit03Icon size={16} />
                </IconButton>
              </Tooltip>
            ) : null}
          </HStack>

          <SettingsRow label={t('version')} value={versionLabel} />

          <Box h="1px" w="full" bg="border.neutral.default" />

          <VStack alignItems="stretch" gap={3} w="full">
            <BodyDefault fontWeight="medium" color="fg.neutral.default">
              {t('environmentalRequirements')}
            </BodyDefault>
            {CIA_KEYS.map((key) => (
              <SettingsRow
                key={key}
                label={t(key)}
                value={t(`requirements.${requirements[key]}`)}
              />
            ))}
          </VStack>

          <Box h="1px" w="full" bg="border.neutral.default" />

          <SettingsRow
            label={t('scorePreview')}
            valueWrap
            value={
              <HStack gap={1} alignItems="center" justifyContent="flex-end">
                <MonoSmall color="fg.neutral.disabled" wordBreak="break-all">
                  {scorePreview}
                </MonoSmall>
                <IconButton
                  variant="ghost"
                  size="xs"
                  aria-label={tCommon('copy')}
                  onClick={() => void copyScorePreview()}
                  color={copiedPreview ? 'fg.success' : undefined}
                >
                  {copiedPreview ? (
                    <Tick01Icon size={16} />
                  ) : (
                    <Copy01Icon size={16} />
                  )}
                </IconButton>
              </HStack>
            }
          />
        </VStack>
      </Box>

      <EditCvssScoreDialog
        open={isDialogOpen}
        isSaving={isSaving}
        isDirty={isDirty}
        draftVersion={draftVersion}
        draftRequirements={draftRequirements}
        showRescoreWarning={showRescoreWarning}
        onOpenChange={setIsDialogOpen}
        onSubmit={() => void saveDialog()}
        onVersionChange={handleVersionChange}
        onRequirementChange={({ key, value }) => {
          setDraftRequirements((current) => ({
            ...current,
            [key]: value,
          }));
        }}
      />
    </VStack>
  );
};
